import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@design-system';
import { AlertCircleIcon, ExternalLink, InfoIcon, RefreshCw, SearchIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { ContactSupportButton } from '@/components/ContactSupportButton';
import { cn } from '@/lib';
import { selectSession } from '@client/model/selectors';
import { SessionType } from '@shared/SessionType';

function JointPolicyComingSoon() {
  return (
    <div className="flex w-full items-center justify-center lg:h-[calc(100vh-320px)]">
      <Empty data-testid="joint-policy-coming-soon">
        <EmptyHeader className="max-w-md">
          <EmptyMedia>
            <div className="bg-primary-50 flex size-12 items-center justify-center rounded-full">
              <InfoIcon className="text-primary size-6" />
            </div>
          </EmptyMedia>
          <EmptyTitle>Joint Policies for Same-Sex Couples Coming Soon</EmptyTitle>
          <EmptyDescription>
            We're actively working on implementing joint policy options for same-sex couples. This
            feature will be available soon. In the meantime, please explore our single policy
            options.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    </div>
  );
}

interface NoQuotesAvailableProps {
  advisorId?: string;
  reason?: QuoteErrorType;
}

const EMPTY_STATE_MESSAGES: Record<
  string,
  { title: string; description: string; showCarrierButton?: boolean }
> = {
  NO_DEMOGRAPHICS_MATCH: {
    title: 'No quotes match your current selections',
    description:
      "We don't have quotes for this combination of age, state, payment structure, and policy type. Try adjusting your payment option or policy type, or contact your advisor.",
    showCarrierButton: false,
  },
  NO_CARRIER_MATCH: {
    title: 'Contact your advisor for assistance',
    description:
      "Quotes exist but aren't available through your advisor's carrier partnerships. Your advisor may need to update carrier settings.",
    showCarrierButton: true,
  },
  default: {
    title: 'No matching quotes found',
    description:
      "We searched but couldn't find any quotes matching your current criteria. Try adjusting your filters or selections.",
    showCarrierButton: true,
  },
};

function NoQuotesAvailable({ advisorId, reason }: NoQuotesAvailableProps) {
  const session = useSelector(selectSession);
  const isAdvisor =
    session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard;

  function handleCarrierSettings() {
    if (!advisorId) return;
    const url = `/advisors/${advisorId}/settings?tab=carrier-bga`;
    window.open(url, '_blank');
  }

  const message =
    reason && EMPTY_STATE_MESSAGES[reason]
      ? EMPTY_STATE_MESSAGES[reason]
      : EMPTY_STATE_MESSAGES.default;

  const showCarrierButton = message.showCarrierButton && isAdvisor;

  return (
    <Empty
      className="border-none py-12"
      data-testid="shop-insurance-no-quotes-message"
    >
      <EmptyHeader>
        <EmptyMedia>
          <div className="bg-primary-50 flex size-12 items-center justify-center rounded-full">
            <SearchIcon className="text-primary size-6" />
          </div>
        </EmptyMedia>
        <EmptyTitle>{message.title}</EmptyTitle>
        <EmptyDescription>{message.description}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent className="max-w-md">
        <div
          className={cn(
            'grid w-full gap-3',
            showCarrierButton ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
          )}
        >
          {showCarrierButton && (
            <Button
              variant="secondary"
              onClick={handleCarrierSettings}
              disabled={!advisorId}
            >
              <ExternalLink />
              Review Carrier Settings
            </Button>
          )}

          <ContactSupportButton
            subject="Missing quotes - Need assistance"
            className="border-input bg-background text-foreground hover:bg-muted inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium"
          />
        </div>
        <div className="rounded-lg bg-amber-50 p-4">
          <p className="text-xs text-amber-800">
            <strong>Note:</strong> Quote availability depends on client demographics, selected
            carriers, and product configurations.
          </p>
        </div>
      </EmptyContent>
    </Empty>
  );
}

interface QuoteErrorDisplayProps {
  error: QuoteError;
  onRetry?: () => void;
}

function QuoteErrorDisplay({ error, onRetry }: QuoteErrorDisplayProps) {
  switch (error.type) {
    case 'AGE_OUT_OF_RANGE':
      return <AgeOutOfRangeError error={error} />;
    case 'STATE_NOT_SUPPORTED':
      return <StateNotSupportedError error={error} />;
    default:
      return (
        <UnexpectedError
          error={error}
          onRetry={onRetry}
        />
      );
  }
}

interface ErrorStateProps {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  showErrorIcon?: boolean;
}

function ErrorState({ title, action, children, showErrorIcon }: ErrorStateProps) {
  return (
    <div className="my-auto flex flex-col items-center justify-center gap-4 py-12">
      <div
        className={cn(
          'mb-4 flex h-16 w-16 items-center justify-center rounded-full',
          showErrorIcon ? 'bg-amber-50' : 'bg-primary-50',
        )}
      >
        {showErrorIcon ? (
          <AlertCircleIcon className="h-8 w-8 text-amber-500" />
        ) : (
          <SearchIcon className="text-primary h-8 w-8" />
        )}
      </div>
      <div className="w-full max-w-md space-y-2 text-center">
        <h3 className="text-primary-dark mb-2 text-lg font-medium">{title}</h3>
        {children}
      </div>
      {action}
    </div>
  );
}

function AgeOutOfRangeError({ error }: { error: QuoteError }) {
  return (
    <ErrorState
      title="We can't show quotes for this age (yet)"
      action={
        <ContactSupportButton
          subject={`Age out of range (${error.clientAge}) - Need assistance`}
          variant="secondary"
        />
      }
    >
      <p className="text-foreground mb-6 max-w-md text-center text-sm">
        Our current quote range is {error.minAge}&ndash;{error.maxAge}. If this client's DOB is
        correct, we can still help.
      </p>
    </ErrorState>
  );
}

function StateNotSupportedError({ error }: { error: QuoteError }) {
  const supportedStatesDisplay = error.supportedStates?.slice(0, 10).join(', ');
  const hasMoreStates = (error.supportedStates?.length || 0) > 10;

  return (
    <ErrorState
      title={`No carrier quotes found for ${error.clientState}`}
      action={
        <ContactSupportButton
          subject={`State not supported (${error.clientState}) - Need assistance`}
          variant="secondary"
        />
      }
    >
      <p className="text-foreground mb-4 text-sm">
        Our carrier partners don't currently offer products in{' '}
        <span className="font-medium">{error.clientState}</span>. We can show quotes in:{' '}
        {supportedStatesDisplay}
        {hasMoreStates && ', and more'}.
      </p>

      <p className="text-muted-foreground mb-6 text-xs">
        If you added extra filters, try removing them and re-run quotes. If you already have a
        product quote in hand, we can still help you upload the policy and analyze it against the
        client's LTC plan.
      </p>
    </ErrorState>
  );
}

function UnexpectedError({ error, onRetry }: { error: QuoteError; onRetry?: () => void }) {
  const supportSubject = error.logCode
    ? `Quote loading error (ID: ${error.logCode})`
    : 'Quote loading error - Need assistance';

  return (
    <ErrorState
      showErrorIcon
      title="We're having trouble loading quotes"
      action={
        <div className="flex flex-col gap-3 sm:flex-row">
          {onRetry && (
            <Button
              variant="secondary"
              onClick={onRetry}
            >
              <RefreshCw />
              Try Again
            </Button>
          )}
          <ContactSupportButton
            subject={supportSubject}
            variant="outline"
          />
        </div>
      }
    >
      <p className="text-foreground mb-6 max-w-md text-center text-sm">
        This looks like an issue on our side. Please refresh and try again.
        {error.logCode && (
          <>
            {' '}
            Error ID:{' '}
            <code className="bg-secondary rounded px-1.5 py-0.5 text-xs font-medium">
              {error.logCode}
            </code>
          </>
        )}
      </p>
    </ErrorState>
  );
}

export { JointPolicyComingSoon, NoQuotesAvailable, QuoteErrorDisplay };
